import { useState, useEffect } from 'react';
import Button from '../base/Button';

interface AccessibilitySettings {
  fontSize: number;
  highContrast: boolean;
  darkMode: boolean;
  reducedMotion: boolean;
  focusOutlines: boolean;
  textSpacing: boolean;
}

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>({
    fontSize: 100,
    highContrast: false,
    darkMode: false,
    reducedMotion: false,
    focusOutlines: false,
    textSpacing: false
  });

  useEffect(() => {
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, []);

  const applySettings = (newSettings: AccessibilitySettings) => {
    const root = document.documentElement;
    
    // Schriftgröße
    root.style.fontSize = `${newSettings.fontSize}%`;
    
    // Hoher Kontrast
    if (newSettings.highContrast) {
      root.classList.add('accessibility-high-contrast');
    } else {
      root.classList.remove('accessibility-high-contrast');
    }
    
    // Dark Mode
    if (newSettings.darkMode) {
      root.classList.add('accessibility-dark-mode');
    } else {
      root.classList.remove('accessibility-dark-mode');
    }
    
    // Reduzierte Bewegung
    if (newSettings.reducedMotion) {
      root.classList.add('accessibility-reduced-motion');
    } else {
      root.classList.remove('accessibility-reduced-motion');
    }
    
    // Fokus-Umrisse
    if (newSettings.focusOutlines) {
      root.classList.add('accessibility-focus-outlines');
    } else {
      root.classList.remove('accessibility-focus-outlines');
    }
    
    // Textabstand
    if (newSettings.textSpacing) {
      root.classList.add('accessibility-text-spacing');
    } else {
      root.classList.remove('accessibility-text-spacing');
    }
  };

  const updateSetting = (key: keyof AccessibilitySettings, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    applySettings(newSettings);
    localStorage.setItem('accessibilitySettings', JSON.stringify(newSettings));
  };

  const resetSettings = () => {
    const defaultSettings: AccessibilitySettings = {
      fontSize: 100,
      highContrast: false,
      darkMode: false,
      reducedMotion: false,
      focusOutlines: false,
      textSpacing: false
    };
    setSettings(defaultSettings);
    applySettings(defaultSettings);
    localStorage.setItem('accessibilitySettings', JSON.stringify(defaultSettings));
  };

  return (
    <>
      {/* Accessibility Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-40 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all duration-200 flex items-center justify-center cursor-pointer focus:outline-none focus:ring-4 focus:ring-blue-300"
        aria-label="Barrierefreiheit-Einstellungen öffnen"
        title="Barrierefreiheit"
      >
        <i className="ri-wheelchair-line text-xl"></i>
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <i className="ri-wheelchair-line text-blue-600 mr-3"></i>
                  Barrierefreiheit
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100"
                  aria-label="Schließen"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* Schriftgröße */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-font-size-2 text-blue-600 mr-2"></i>
                    Schriftgröße
                  </h3>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => updateSetting('fontSize', Math.max(80, settings.fontSize - 10))}
                      className="w-10 h-10 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center cursor-pointer"
                      aria-label="Schrift verkleinern"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="text-sm font-medium min-w-[60px] text-center">
                      {settings.fontSize}%
                    </span>
                    <button
                      onClick={() => updateSetting('fontSize', Math.min(150, settings.fontSize + 10))}
                      className="w-10 h-10 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center cursor-pointer"
                      aria-label="Schrift vergrößern"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>

                {/* Hoher Kontrast */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="ri-contrast-2-line text-blue-600 mr-2"></i>
                      <div>
                        <h3 className="font-semibold text-gray-900">Hoher Kontrast</h3>
                        <p className="text-sm text-gray-600">Verbessert die Lesbarkeit</p>
                      </div>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.highContrast}
                        onChange={(e) => updateSetting('highContrast', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-12 h-6 rounded-full transition-colors ${
                        settings.highContrast ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                          settings.highContrast ? 'translate-x-6' : 'translate-x-0.5'
                        } mt-0.5`}></div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Dark Mode */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="ri-moon-line text-blue-600 mr-2"></i>
                      <div>
                        <h3 className="font-semibold text-gray-900">Dunkler Modus</h3>
                        <p className="text-sm text-gray-600">Reduziert Augenbelastung</p>
                      </div>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.darkMode}
                        onChange={(e) => updateSetting('darkMode', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-12 h-6 rounded-full transition-colors ${
                        settings.darkMode ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                          settings.darkMode ? 'translate-x-6' : 'translate-x-0.5'
                        } mt-0.5`}></div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Reduzierte Bewegung */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="ri-pause-circle-line text-blue-600 mr-2"></i>
                      <div>
                        <h3 className="font-semibold text-gray-900">Reduzierte Bewegung</h3>
                        <p className="text-sm text-gray-600">Weniger Animationen</p>
                      </div>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.reducedMotion}
                        onChange={(e) => updateSetting('reducedMotion', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-12 h-6 rounded-full transition-colors ${
                        settings.reducedMotion ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                          settings.reducedMotion ? 'translate-x-6' : 'translate-x-0.5'
                        } mt-0.5`}></div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Fokus-Umrisse */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="ri-focus-3-line text-blue-600 mr-2"></i>
                      <div>
                        <h3 className="font-semibold text-gray-900">Fokus-Umrisse</h3>
                        <p className="text-sm text-gray-600">Bessere Tastaturnavigation</p>
                      </div>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.focusOutlines}
                        onChange={(e) => updateSetting('focusOutlines', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-12 h-6 rounded-full transition-colors ${
                        settings.focusOutlines ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                          settings.focusOutlines ? 'translate-x-6' : 'translate-x-0.5'
                        } mt-0.5`}></div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Textabstand */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="ri-text-spacing text-blue-600 mr-2"></i>
                      <div>
                        <h3 className="font-semibold text-gray-900">Textabstand</h3>
                        <p className="text-sm text-gray-600">Größere Zeilenabstände</p>
                      </div>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.textSpacing}
                        onChange={(e) => updateSetting('textSpacing', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-12 h-6 rounded-full transition-colors ${
                        settings.textSpacing ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                          settings.textSpacing ? 'translate-x-6' : 'translate-x-0.5'
                        } mt-0.5`}></div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Reset Button */}
                <div className="pt-4 border-t border-gray-200">
                  <Button
                    onClick={resetSettings}
                    variant="outline"
                    className="w-full"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    Alle Einstellungen zurücksetzen
                  </Button>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                    <i className="ri-information-line mr-2"></i>
                    Tastaturnavigation
                  </h4>
                  <p className="text-sm text-blue-800">
                    Verwenden Sie die Tab-Taste zur Navigation und Enter zum Aktivieren von Elementen. 
                    Drücken Sie Escape, um Dialoge zu schließen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;